'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState } from 'react';

import paths from '@/paths';
import { ChevronRight, MenuIcon } from '@/components/icons';
import { useHeaderMenuContent } from '@/lib/context/HeaderMenuContentContext';
import { useDestinationsMenu } from '@/components/header/useDestinationsMenu';
import { countryHref, getVisibleExperiences } from '@/components/header/navShared';
import { MobileNavPanel } from './MobileNavPanel';

/** Panel views: the root menu, plus the two API-driven lists it drills into. */
type View = 'root' | 'destinations' | 'experiences';

const VIEW_TITLES: Record<View, string> = {
  root: 'Menu',
  destinations: 'Destinations',
  experiences: 'Experiences',
};

const sectionLabelClass =
  'px-1 pb-2 pt-1 font-inter text-xs font-semibold uppercase tracking-wider text-gray-500';

/** One row model for every menu entry — links and drill-down buttons share the same box. */
const rowClass = (active: boolean) =>
  [
    'flex w-full cursor-pointer appearance-none items-center justify-between gap-3 rounded-lg border-0 bg-transparent px-3 py-3 text-left font-inter text-lg transition-colors active:bg-gray-800/60',
    active ? 'bg-gray-800 font-medium text-primary-400' : 'font-normal text-gray-100 hover:bg-gray-800',
  ].join(' ');

const messageClass = 'px-3 py-4 font-inter text-sm text-gray-500';

function DestinationsPanelContent({ onNavigate }: { onNavigate: () => void }) {
  const {
    loading,
    loadError,
    content,
    continents,
    selectedContinent,
    setSelectedContinentId,
    tourCountries,
    visibleCountries,
  } = useDestinationsMenu();

  if (loading) return <p className={messageClass}>Loading destinations…</p>;
  if (loadError) return <p className={messageClass}>Could not load destinations.</p>;
  if (content && continents.length === 0 && tourCountries.length === 0) {
    return <p className={messageClass}>No destinations available.</p>;
  }

  return (
    <div className="flex flex-col gap-6">
      {continents.length > 0 && (
        <section>
          <p className={sectionLabelClass}>Continents</p>
          <ul className="m-0 list-none p-0">
            {continents.map((continent) => {
              const active = selectedContinent?.id === continent.id;
              return (
                <li key={continent.id}>
                  <button
                    type="button"
                    aria-pressed={active}
                    onClick={() => setSelectedContinentId(continent.id)}
                    className={rowClass(active)}
                  >
                    {continent.name}
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <section>
        <p className={sectionLabelClass}>Countries</p>
        <ul className="m-0 list-none p-0">
          {visibleCountries.length === 0 ? (
            <li className={messageClass}>No countries in this region.</li>
          ) : (
            visibleCountries.map((country) => (
              <li key={country.id}>
                <Link href={countryHref(country)} className={rowClass(false)} onClick={onNavigate}>
                  {country.name}
                </Link>
              </li>
            ))
          )}
        </ul>
      </section>
    </div>
  );
}

function ExperiencesPanelContent({ onNavigate }: { onNavigate: () => void }) {
  const { content, contentLoading, contentError } = useHeaderMenuContent();
  const experiences = getVisibleExperiences(content);

  if (contentLoading) return <p className={messageClass}>Loading…</p>;
  if (contentError) return <p className={messageClass}>Could not load experiences.</p>;
  if (experiences.length === 0) return <p className={messageClass}>No experiences available.</p>;

  return (
    <ul className="m-0 list-none p-0">
      {experiences.map((experience) => (
        <li key={experience.id}>
          <Link href={paths.experience(experience.slug)} className={rowClass(false)} onClick={onNavigate}>
            {experience.name}
          </Link>
        </li>
      ))}
    </ul>
  );
}

function RootMenu({
  onDrillDown,
  onNavigate,
}: {
  onDrillDown: (view: Exclude<View, 'root'>) => void;
  onNavigate: () => void;
}) {
  const pathname = usePathname();

  return (
    <nav className="flex flex-col gap-6" aria-label="Main">
      <section>
        <p className={sectionLabelClass} id="mobile-nav-explore">
          Explore
        </p>
        <ul className="m-0 list-none p-0" aria-labelledby="mobile-nav-explore">
          <li>
            <button
              type="button"
              onClick={() => onDrillDown('destinations')}
              className={rowClass(pathname.startsWith('/destinations/'))}
            >
              <span>Destinations</span>
              <ChevronRight size={18} opacity={1} />
            </button>
          </li>
          <li>
            <button
              type="button"
              onClick={() => onDrillDown('experiences')}
              className={rowClass(pathname.startsWith('/experiences/'))}
            >
              <span>Experiences</span>
              <ChevronRight size={18} opacity={1} />
            </button>
          </li>
          <li>
            <Link
              href={paths.tours()}
              onClick={onNavigate}
              className={rowClass(pathname === paths.tours())}
            >
              Tours
            </Link>
          </li>
        </ul>
      </section>

      <section>
        <p className={sectionLabelClass} id="mobile-nav-company">
          Company
        </p>
        <ul className="m-0 list-none p-0" aria-labelledby="mobile-nav-company">
          <li>
            <Link href="/about-us" onClick={onNavigate} className={rowClass(pathname === '/about-us')}>
              About us
            </Link>
          </li>
          <li>
            <Link href="/contact" onClick={onNavigate} className={rowClass(pathname === '/contact')}>
              Contact us
            </Link>
          </li>
        </ul>
      </section>
    </nav>
  );
}

/**
 * Mobile primary navigation: a hamburger button (below `lg`) that opens the full-screen
 * nav panel. Desktop keeps the hover mega-menus in <HeaderNavLinks />.
 */
export function MobileNav() {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>('root');

  const close = () => setOpen(false);

  const toggle = () => {
    if (open) {
      close();
      return;
    }
    // Reset on open (not on close) so the panel never changes view mid-exit-animation.
    setView('root');
    setOpen(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={toggle}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={open ? 'Close navigation menu' : 'Open navigation menu'}
        className="flex shrink-0 appearance-none rounded-full border-0 bg-transparent p-0 outline-none focus-visible:ring-1 focus-visible:ring-primary-400 lg:hidden"
      >
        <MenuIcon active={open} />
      </button>

      <MobileNavPanel
        open={open}
        title={VIEW_TITLES[view]}
        onClose={close}
        onBack={view === 'root' ? undefined : () => setView('root')}
      >
        {view === 'destinations' ? (
          <DestinationsPanelContent onNavigate={close} />
        ) : view === 'experiences' ? (
          <ExperiencesPanelContent onNavigate={close} />
        ) : (
          <RootMenu onDrillDown={setView} onNavigate={close} />
        )}
      </MobileNavPanel>
    </>
  );
}
